import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';

interface StaffInvite {
  id: string;
  careHome: {
    id: string;
    name: string;
    address?: string;
  };
  staff: {
    id: string;
    email: string;
    name?: string;
    isAdmin: boolean;
  };
  expiresAt: string;
  acceptedAt?: string;
}

export function StaffInvitePage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [invite, setInvite] = useState<StaffInvite | null>(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (token) {
      loadInvite();
    }
  }, [token]);

  const loadInvite = async () => {
    try {
      const data = await api.getStaffInvite(token!);
      setInvite(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load invite');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    setAccepting(true);
    setError('');

    try {
      await api.acceptStaffInvite(token!);
      navigate('/staff/dashboard');
    } catch (err: any) {
      setError(err.message || 'Failed to accept invite');
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-600">Loading invite...</div>
      </div>
    );
  }

  if (error && !invite) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
        <div className="max-w-md w-full">
          <div className="card">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Invalid Invite</h2>
            <p className="text-gray-600 mb-6">{error || 'This invite link is not valid.'}</p>
            <button onClick={() => navigate('/')} className="btn btn-primary w-full">
              Go to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (invite?.acceptedAt) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
        <div className="max-w-md w-full">
          <div className="card">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Invite Already Accepted</h2>
            <p className="text-gray-600 mb-6">
              This invite has already been accepted. You can access your dashboard below.
            </p>
            <button onClick={() => navigate('/staff/dashboard')} className="btn btn-primary w-full">
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  const isExpired = invite && new Date(invite.expiresAt) < new Date();

  if (isExpired) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
        <div className="max-w-md w-full">
          <div className="card">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Invite Expired</h2>
            <p className="text-gray-600 mb-6">
              This invite has expired. Please contact your administrator to receive a new invite.
            </p>
            <button onClick={() => navigate('/')} className="btn btn-primary w-full">
              Go to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
      <div className="max-w-md w-full">
        <div className="card">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Staff Invitation</h1>
          <p className="text-gray-600 mb-6">
            You've been invited to join <strong>{invite?.careHome.name}</strong> as a{' '}
            <strong>{invite?.staff.isAdmin ? 'Administrator' : 'Staff Member'}</strong>.
          </p>

          {invite?.careHome.address && (
            <div className="mb-6 p-3 bg-gray-50 rounded-lg">
              <div className="text-sm text-gray-500">Location</div>
              <div className="text-gray-900">{invite.careHome.address}</div>
            </div>
          )}

          {error && (
            <div className="mb-4 rounded-md bg-red-50 p-4">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          <div className="space-y-3">
            <button
              onClick={handleAccept}
              disabled={accepting}
              className="w-full btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {accepting ? 'Accepting...' : 'Accept Invitation'}
            </button>
            <button onClick={() => navigate('/')} className="w-full btn btn-secondary">
              Decline
            </button>
          </div>

          <div className="mt-6 pt-6 border-t border-gray-200">
            <h3 className="text-sm font-medium text-gray-900 mb-2">
              {invite?.staff.isAdmin ? 'Administrator Access' : 'Staff Access'}
            </h3>
            <div className="text-xs text-gray-600 space-y-1">
              {invite?.staff.isAdmin ? (
                <>
                  <p>• Full access to all care home data</p>
                  <p>• Can manage staff members</p>
                  <p>• Can create and manage schedules</p>
                  <p>• Can edit all residents, tasks, and medications</p>
                </>
              ) : (
                <>
                  <p>• View all residents you're scheduled for</p>
                  <p>• Edit tasks and log medications during your shifts</p>
                  <p>• View your schedule and assigned tasks</p>
                  <p>• Read-only access to non-scheduled residents</p>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
